import * as Tooltip from '~/components/ui/Tooltip';

export function TopPages({ rows }: { rows: Array<{ url: string; count: number }> }) {
  return (
    <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
      <div className="flex items-center justify-between border-b border-bolt-ds-borderDivider pb-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
            Top pages
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className="i-ph:info-fill" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="top" align="center" sideOffset={6}>
                    Pages with the most pageviews. Click a row to open.
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-bolt-ds-borderDivider">
        {rows.map((row, idx) => (
          <a
            key={`top-page-${idx}`}
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between py-2 hover:underline"
          >
            <span className="truncate max-w-[75%] text-sm">{row.url}</span>
            <span className="text-xs text-bolt-ds-textSecondary font-mono tabular-nums">
              {row.count.toLocaleString()}
            </span>
          </a>
        ))}
        {rows.length === 0 && <div className="text-xs text-bolt-ds-textTertiary py-4">No data</div>}
      </div>
    </div>
  );
}

export function TopSources({ rows }: { rows: Array<{ label: string; url: string; count: number }> }) {
  return (
    <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
      <div className="flex items-center justify-between border-b border-bolt-ds-borderDivider pb-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
            Top sources
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className="i-ph:info-fill" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="top" align="center" sideOffset={6}>
                    Referring URLs that generated the most traffic. Click a row to open.
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-bolt-ds-borderDivider">
        {rows.map((row, idx) =>
          row.url ? (
            <a
              key={`top-source-${idx}`}
              href={row.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between py-2 hover:underline"
            >
              <span className="truncate max-w-[75%] text-sm">{row.label}</span>
              <span className="text-xs text-bolt-ds-textSecondary font-mono tabular-nums">
                {row.count.toLocaleString()}
              </span>
            </a>
          ) : (
            <div key={`top-source-${idx}`} className="flex items-center justify-between py-2">
              <span className="truncate max-w-[75%] text-sm">{row.label}</span>
              <span className="text-xs text-bolt-ds-textSecondary font-mono tabular-nums">
                {row.count.toLocaleString()}
              </span>
            </div>
          ),
        )}
        {rows.length === 0 && <div className="text-xs text-bolt-ds-textTertiary py-4">No data</div>}
      </div>
    </div>
  );
}

export function TopCountries({ rows }: { rows: Array<{ code: string; name: string; count: number }> }) {
  return (
    <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
      <div className="flex items-center justify-between border-b border-bolt-ds-borderDivider pb-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">Top locations</div>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-bolt-ds-borderDivider">
        {rows.map((row, idx) => (
          <div key={`top-country-${idx}`} className="flex items-center justify-between py-2">
            <span className="truncate max-w-[75%] text-sm">{row.name}</span>
            <span className="text-xs text-bolt-ds-textSecondary font-mono tabular-nums">
              {row.count.toLocaleString()}
            </span>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-bolt-ds-textTertiary py-4">No data</div>}
      </div>
    </div>
  );
}

export function TopNotFound({ rows }: { rows: Array<{ url: string; count: number }> }) {
  return (
    <div className="flex flex-col gap-2 bg-bolt-ds-surfaceTwo border border-bolt-ds-borderDivider rounded-lg p-4">
      <div className="flex items-center justify-between border-b border-bolt-ds-borderDivider pb-2">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 text-sm font-semibold text-bolt-ds-textPrimary">
            Top resources not found (404)
            <Tooltip.Provider>
              <Tooltip.Root>
                <Tooltip.Trigger asChild>
                  <span className="i-ph:info-fill" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="top" align="center" sideOffset={6}>
                    Requested resources that returned 404 most often. Click a row to open.
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          </div>
        </div>
      </div>
      <div className="flex flex-col divide-y divide-bolt-ds-borderDivider">
        {rows.map((row, idx) => (
          <a
            key={`top-nf-${idx}`}
            href={row.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between py-2 hover:underline"
          >
            <span className="truncate max-w-[75%] text-sm">{row.url}</span>
            <span className="text-xs text-bolt-ds-textSecondary font-mono tabular-nums">
              {row.count.toLocaleString()}
            </span>
          </a>
        ))}
        {rows.length === 0 && <div className="text-xs text-bolt-ds-textTertiary py-4">No data</div>}
      </div>
    </div>
  );
}
